import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  logStorylandReading,
  generateTTS,
  getBookContents,
  getStoryLandBookInfo,
} from "../api/LibraryAPI";
import { decryptUrlSafe } from "../utils/encryption";
import { ERROR } from "../utils/Constants";

// 분리된 컴포넌트 임포트
import SharingPC from "../components/sharing/SharingPC";
import SharingMobile from "../components/sharing/SharingMobile";

const Sharing = () => {
  const location = useLocation();
  const params = new URLSearchParams(window.location.search);
  const navigate = useNavigate();

  const USER_ID = localStorage.getItem("userID");
  const BOOK_ID = decryptUrlSafe(params.get("book_id"));

  const pageName = location.state?.pageName ?? "";
  const stateBookID =
    location.state?.bookList?.book_id ?? location.state?.bookID ?? null;
  const lsBookID = decryptUrlSafe(params.get("book_id"));
  const bookID = stateBookID ?? lsBookID ?? "";

  const [bookData, setBookData] = useState(null);
  const [story, setStory] = useState([]);
  const [index, setIndex] = useState(-1);
  const [showFinalReadingModal, setShowFinalReadingModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [bgImage, setBgImage] = useState("");
  const [contentText, setContentText] = useState([]);
  const [speechState, setSpeechState] = useState(false);
  const [audioCache, setAudioCache] = useState({});
  const [imageSrc, setImageSrc] = useState("/images/utils/button/music-on.svg");
  const [currentAudioIndex, setCurrentAudioIndex] = useState(null);

  // 오디오북 관련 상태
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [isFlipping, setIsFlipping] = useState(false);
  const [isBookOpened, setIsBookOpened] = useState(false);
  const [showFlipbook, setShowFlipbook] = useState(false);
  const [showTitleOnCover, setShowTitleOnCover] = useState(true);

  // 모바일 감지 상태
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const audioRef = useRef(new Audio());
  const fetchingRef = useRef({});

  // 화면 크기 변화 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const callBookContentsAPI = async (book_id, isStoryland = false) => {
    const res = isStoryland
      ? await getStoryLandBookInfo(book_id)
      : await getBookContents(book_id);

    if (res.success) {
      const raw = res.data;
      const book = Array.isArray(raw) ? raw[0] : raw;
      setBookData(book);
      setStory(book.contents_json?.story || []);
      setBgImage(book.bookcover);
      setContentText(book.title);
    } else {
      setErrorMessage(ERROR.lack);
      setShowErrorModal(true);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const isStoryland = pageName === "storyland";
      if (isStoryland) {
        const res = await logStorylandReading(USER_ID, bookID);
        if (!res.success) {
          setErrorMessage(ERROR.lack);
          setShowErrorModal(true);
          return;
        }
      }
      await callBookContentsAPI(bookID, isStoryland);
      if (location.state?.makeAudioBook) {
        setShowAudioModal(true);
      }
    };
    fetchData();
  }, []);

  const fetchTTS = async (pageIndex) => {
    if (audioCache[pageIndex] || fetchingRef.current[pageIndex]) return;

    fetchingRef.current[pageIndex] = true;
    let textContent =
      pageIndex === -1 ? bookData.title : story[pageIndex]?.page_content || "";

    try {
      const response = await generateTTS(USER_ID, textContent, "vdain", 0);
      if (response.success) {
        const audioBlob = new Blob([response.data], { type: "audio/mpeg" });
        const audioURL = URL.createObjectURL(audioBlob);
        setAudioCache((prev) => ({ ...prev, [pageIndex]: audioURL }));
      }
    } catch (error) {
      console.error("TTS 요청 오류:", error);
    } finally {
      fetchingRef.current[pageIndex] = false;
    }
  };

  const handleButtonClick = (direction) => {
    setIndex((prevIndex) => {
      let newIndex = prevIndex + (direction === "left" ? -1 : 1);
      if (newIndex < -1) {
        newIndex = story.length - 1;
      } else if (newIndex >= story.length) {
        setShowFinalReadingModal(true);
        return prevIndex;
      }
      return newIndex;
    });
  };

  const handleSpeechState = () => {
    if (!speechState) {
      setImageSrc("/images/utils/button/music-off.svg");
      if (!audioCache[index]) fetchTTS(index);
    } else {
      audioRef.current.pause();
      setImageSrc("/images/utils/button/music-on.svg");
    }
    setSpeechState((prev) => !prev);
  };

  useEffect(() => {
    if (!bookData) return;

    if (index === -1) {
      setBgImage(bookData.bookcover);
      setContentText(bookData.title);
      setShowFinalReadingModal(false);
      // 추가 스타일 상태 초기화
      if (setIsBookOpened) setIsBookOpened(false);
      if (setShowFlipbook) setShowFlipbook(false);
    } else {
      setBgImage(story[index]?.background || "");
      let contents = story[index]?.page_content || "";
      if (contents.includes("\\")) contents = contents.replaceAll("\\", "");
      const sentences = contents.split(". ").map((s) => s.trim() + ".");
      setContentText(sentences);
    }

    if (speechState) {
      if (!audioCache[index]) fetchTTS(index);
      if (index + 1 < story.length && !audioCache[index + 1])
        fetchTTS(index + 1);
    }
  }, [index, bookData]);

  useEffect(() => {
    if (speechState && audioCache[index] && index !== currentAudioIndex) {
      audioRef.current.pause();
      audioRef.current.src = audioCache[index];
      audioRef.current
        .play()
        .then(() => {
          setCurrentAudioIndex(index);
        })
        .catch((error) => console.error("Audio play error:", error));
    }
  }, [audioCache, index, speechState]);

  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      try {
        if (audio) {
          audio.pause();
          audio.src = "";
        }
      } catch (err) {
        console.warn("Audio cleanup error", err);
      }
    };
  }, []);

  const handleAudioEnd = () => {
    setImageSrc("/images/utils/button/music-on.svg");
    setSpeechState(false);
  };

  const handleExit = () => {
    setAudioCache({});
    setShowFinalReadingModal(false);
  };

  // 모든 자식 컴포넌트에 넘겨줄 공통 Props
  const commonProps = {
    bookData,
    story,
    index,
    setIndex,
    showFinalReadingModal,
    setShowFinalReadingModal,
    showErrorModal,
    setShowErrorModal,
    errorMessage,
    bgImage,
    contentText,
    speechState,
    imageSrc,
    isFlipping,
    setIsFlipping,
    isBookOpened,
    setIsBookOpened,
    showFlipbook,
    setShowFlipbook,
    showTitleOnCover,
    setShowTitleOnCover,
    showAudioModal,
    setShowAudioModal,
    handleButtonClick,
    handleSpeechState,
    handleAudioEnd,
    handleExit,
    audioRef,
    pageName,
    navigate,
    USER_ID,
  };

  return isMobile ? (
    <SharingMobile {...commonProps} />
  ) : (
    <SharingPC {...commonProps} />
  );
};

export default Sharing;
